/**
 * @lines98/client/lib/format — small shared formatting helpers reused across
 * screens (модель «ствол → ветки»: общая логика в модуле, не копируется в экран —
 * owner-directive 2026-05-30 + code-structure-conventions/SKILL.md).
 *
 * Currently:
 *   - `formatDateLabel(isoDate)` — `YYYY-MM-DD` → localized day label («22 мая»).
 *     Consumed by the daily-board screen header eyebrow AND the daily leaderboard
 *     header (T-8 wiring). Single source so the same day renders identically on
 *     both surfaces (core-daily-board.md §2.4).
 *   - `formatRuNumber(n)` — RU-locale grouped integer («21 400»).
 *   - `formatRewardPair(gold, shards)` — компактная подпись награды «+G зол. · +S оск.»
 *     (нулевые валюты пропускаются; полностью нулевая → ''). ЕДИНЫЙ источник формата
 *     награды — переиспользуется блоком добычи (reward-block) И окошком ачивок на итогах
 *     (achievement-result-window). Раньше был private в reward-block — вынесен сюда
 *     (R3 тикета fe-achievement-result-window, owner-directive 2026-05-30 «общее в модуль»).
 *   - `escapeHtml(s)` — minimal HTML-attribute/text escape for template strings.
 *
 * NOTE (scope): `escapeHtml` / `formatRuNumber` are also still inlined in several
 * older screens (pre-existing duplication). This module is the canonical home;
 * new code imports from here, and older screens migrate opportunistically — a
 * full sweep is out of scope for the leaderboard-wiring ticket.
 *
 * i18n (gap-extract wave 7): date/number formatting follows the ACTIVE UI locale
 * (`getLocale()` — single source of truth, ADR-0021), not a hardcoded `ru-RU`.
 * The reward-pair abbreviations come from the catalog (`game.currency.*Short`).
 */

import { getLocale, t } from './i18n/index.js';

/**
 * Format a `YYYY-MM-DD` ISO date к a localized day label («22 мая» / «May 22»).
 *
 * Anchors к a neutral UTC midday so a timezone shift can't roll the displayed
 * day. Locale = ACTIVE UI locale (`getLocale()`) so the same date renders in the
 * player's language on EVERY surface (was hardcoded `ru-RU` → русская дата на всех
 * языках; gap-extract wave 7 fix). Falls back к the raw ISO string if `Intl` is
 * unavailable.
 *
 * Per core-daily-board.md §2.4: «локализованный лейбл даты … формат — UX-решение
 * копирайтера/локализатора».
 */
export function formatDateLabel(isoDate: string): string {
  try {
    const parts = isoDate.split('-').map(Number);
    const d = new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!, 12, 0, 0));
    return new Intl.DateTimeFormat(getLocale(), {
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(d);
  } catch {
    return isoDate;
  }
}

/** Format an integer with active-locale grouping («21 400» / «21,400»). */
export function formatRuNumber(n: number): string {
  return n.toLocaleString(getLocale());
}

/**
 * Относительная дата от ISO-8601 UTC к «сейчас» через `Intl.RelativeTimeFormat`
 * (локализуемо, без хардкод-строк). Единый источник относительного времени —
 * переиспользуется списком «Книги реплеев» (строки альбомов, AC-A5: «относительная
 * дата»). Грубое округление по крупнейшей единице (сегодня/N дней/N недель/N месяцев/
 * N лет) — для списка достаточно; абсолютная дата живёт в `formatDateLabel`. Падение
 * `Intl` / битый ISO → fallback на `formatDateLabel` (абсолютная дата, не пусто).
 *
 * @param iso  ISO-8601 UTC строка (напр. `finished_at`/`saved_at`).
 * @param lang BCP-47 локаль (по умолчанию — активная UI-локаль `getLocale()`).
 * @param now  «сейчас» в ms (инжектируемые часы — детерминизм/тест; default Date.now()).
 */
export function formatRelativeDate(iso: string, lang = getLocale(), now: number = Date.now()): string {
  try {
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return formatDateLabel(iso);
    const diffMs = then - now; // прошлое → отрицательное
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    const sec = diffMs / 1000;
    const absSec = Math.abs(sec);
    const MIN = 60;
    const HOUR = 3600;
    const DAY = 86_400;
    const WEEK = DAY * 7;
    const MONTH = DAY * 30;
    const YEAR = DAY * 365;
    if (absSec < HOUR) return rtf.format(Math.round(sec / MIN), 'minute');
    if (absSec < DAY) return rtf.format(Math.round(sec / HOUR), 'hour');
    if (absSec < WEEK) return rtf.format(Math.round(sec / DAY), 'day');
    if (absSec < MONTH) return rtf.format(Math.round(sec / WEEK), 'week');
    if (absSec < YEAR) return rtf.format(Math.round(sec / MONTH), 'month');
    return rtf.format(Math.round(sec / YEAR), 'year');
  } catch {
    return formatDateLabel(iso);
  }
}

/**
 * Компактная подпись награды «+G зол. · +S оск.» (R3 fe-achievement-result-window).
 * Пропускает нулевые валюты; полностью нулевая награда → пустая строка (нечего
 * показывать). ЕДИНЫЙ источник формата — переиспользуется блоком добычи И окошком
 * ачивок на итогах (НЕ копируется в каждый — code-structure §3 «один источник правды»).
 */
export function formatRewardPair(gold: number, shards: number): string {
  const parts: string[] = [];
  if (gold > 0) parts.push(t('game.currency.goldShort', { n: formatRuNumber(gold) }));
  if (shards > 0) parts.push(t('game.currency.shardsShort', { n: formatRuNumber(shards) }));
  return parts.join(' · ');
}

/** Minimal HTML escape for interpolating user/derived text into template strings. */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&'
      ? '&amp;'
      : ch === '<'
        ? '&lt;'
        : ch === '>'
          ? '&gt;'
          : '&quot;',
  );
}
